/**
 * localStorage keys, namespaced per restaurant.
 *
 * A guest can have an open tab at two restaurants in the same browser, so every
 * persisted key carries the tenant slug. The old single-tenant keys
 * (`butter_order_state_v1`, `butter_pending_order_id`) are intentionally NOT
 * migrated — their cart lines predate server-side pricing and `source`.
 */

const safeSlug = (slug) => String(slug || "unknown").replace(/[^a-z0-9_-]/gi, "");

/** Cart + guest details for one restaurant. */
export const orderStateKey = (slug) => `fuelly_${safeSlug(slug)}_order_state_v2`;

/** `{ orderId, claimToken }` of an anonymous order awaiting a post-sign-in claim. */
export const pendingOrderKey = (slug) => `fuelly_${safeSlug(slug)}_pending_order_id`;

/**
 * The quiz answers this device gave last time, per tenant.
 *
 * Local, not session: the point is the guest who comes back next week. Kept per
 * restaurant because "Very Hungry, beef" at a steakhouse says nothing useful at
 * the place next door, and kept on the device rather than the account because
 * most guests never sign in.
 */
export const lastQuizKey = (slug) => `fuelly_${safeSlug(slug)}_last_quiz`;

/**
 * "This guest has waved the Auto Plate hint away" — sessionStorage, per tenant.
 *
 * Session, not local: the hint is for somebody deciding what to eat right now,
 * and a guest who dismissed it three weeks ago is a different visit. A banner
 * that cannot be got rid of for the length of one meal is the thing that turns
 * a suggestion into a nuisance.
 */
export const aiHintDismissedKey = (slug) => `fuelly_${safeSlug(slug)}_ai_hint_dismissed`;

/**
 * The interface language ("en" | "ru" | "id").
 *
 * The only key here that is deliberately NOT namespaced by tenant: the language a
 * guest reads is theirs, not the restaurant's, and switching from one Fuelly
 * storefront to another must not put them back into a language they cannot read.
 */
export const languageKey = "fuelly_language";
